import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import Footer from '../components/Footer';
import { APP_DASHBOARD_URL } from '../lib/appUrl';
import {
  ArrowLeft,
  Search,
  HelpCircle,
  Sparkles,
  MessageCircle,
  Mail,
} from 'lucide-react';

const QUESTIONS = [
  {
    q: 'What does VibeSprint do?',
    a: "VibeSprint is a decision engine for product feedback. An automated analysis engine sorts signals into Build Next, Fix Now, or Ignore, with effort and revenue-impact estimates on Build Next cards.",
  },
  {
    q: 'Who is it for?',
    a: "Founders and small product teams who want a prioritized backlog from noisy feedback — not a public voting board.",
  },
  {
    q: 'How does it compare to other feedback tools?',
    a: "Feedback tools collect requests, votes, and comments. VibeSprint sits on the decision side: connect Reddit, Hacker News, or RSS and get a ranked Build / Fix / Ignore view. It does not ship a customer-facing board or widget.",
  },
  {
    q: 'What does it cost?',
    a: "The free plan is 10 analyses/day and one workspace, with no card. Mini is $9/month ($7 billed annually) for 80 analyses; Basic $24/$19 for 250; Pro $59/$47 for 600. Paid checkout is not yet available.",
  },
  {
    q: 'What counts as one analysis?',
    a: "One analysis is one import batch, however many signals are inside — not one per signal. Connecting a Signal Source, or a later poll of that source, does not count toward quota. The free plan is capped at 10 analyses/day; paid plans list monthly quotas.",
  },
  {
    q: 'How do I get started?',
    a: "Open the app, load sample data on an empty lane if you want a walkthrough, then connect a Signal Source. No signup or card required.",
  },
  {
    q: 'Can I cancel or change plans?',
    a: "Yes. Monthly plans can be cancelled anytime; you keep access until the period ends. Annual plans have a 14-day money-back guarantee. Paid checkout is not yet available.",
  },
  {
    q: 'Where should I look for data and security details?',
    a: (
      <>
        See the{' '}
        <a href="/privacy" className="text-primary underline underline-offset-2">
          Privacy Policy
        </a>{' '}
        for storage, retention, and deletion, and the{' '}
        <a href="/terms" className="text-primary underline underline-offset-2">
          Terms of Service
        </a>{' '}
        for account and acceptable-use rules.
      </>
    ),
    searchText:
      'See the Privacy Policy for storage, retention, and deletion, and the Terms of Service for account and acceptable-use rules.',
  },
];

const FAQPage = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');

  const filteredQuestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return QUESTIONS;
    return QUESTIONS.filter((item) => {
      const body =
        typeof item.a === 'string' ? item.a : item.searchText || '';
      return item.q.toLowerCase().includes(q) || body.toLowerCase().includes(q);
    });
  }, [query]);

  const totalResults = filteredQuestions.length;

  return (
    <div className="min-h-screen bg-background">
      {/* Top bar */}
      <div className="border-b bg-card sticky top-0 z-50">
        <div className="container-wide mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
              data-testid="faq-back-button"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <h1 className="text-xl font-bold flex items-center gap-2">
              <HelpCircle className="h-5 w-5 text-primary" />
              FAQ
            </h1>
          </div>
          <Button
            asChild
            variant="outline"
            size="sm"
            data-testid="faq-dashboard-link"
          >
            <a href={APP_DASHBOARD_URL}>Open dashboard</a>
          </Button>
        </div>
      </div>

      {/* Hero */}
      <section className="py-12 sm:py-16 border-b">
        <div className="container mx-auto px-4 max-w-3xl text-center">
          <Badge
            variant="outline"
            className="mb-4 border-primary/30 text-primary"
            data-testid="faq-hero-badge"
          >
            <Sparkles className="h-3 w-3 mr-1" />
            Got questions? We've got answers.
          </Badge>
          <h2 className="text-4xl sm:text-5xl font-bold tracking-tight mb-4">
            Frequently asked questions
          </h2>
          <p className="text-lg text-muted-foreground mb-8">
            Short answers on what VibeSprint does, who it is for, and how plans work.
          </p>

          {/* Search */}
          <div className="relative max-w-xl mx-auto">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search questions..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-10 h-11"
              data-testid="faq-search-input"
            />
          </div>

          {query && (
            <p
              className="mt-3 text-sm text-muted-foreground"
              data-testid="faq-search-results-count"
            >
              {totalResults === 0
                ? "No questions match your search."
                : `${totalResults} question${totalResults === 1 ? '' : 's'} found`}
            </p>
          )}
        </div>
      </section>

      {/* Questions */}
      <section className="py-12">
        <div className="container mx-auto px-4 max-w-3xl">
          {filteredQuestions.length === 0 ? (
            <Card className="p-10 text-center" data-testid="faq-empty-state">
              <HelpCircle className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
              <h3 className="text-lg font-semibold mb-1">
                No matches found
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Try a different search term, or reach out and we'll help directly.
              </p>
              <Button
                variant="outline"
                onClick={() => setQuery('')}
                data-testid="faq-reset-button"
              >
                Reset filters
              </Button>
            </Card>
          ) : (
            <Accordion type="single" collapsible className="space-y-2" data-testid="faq-list">
              {filteredQuestions.map((item, i) => (
                <AccordionItem
                  key={item.q}
                  value={`faq-${i}`}
                  className="border rounded-lg px-4 bg-card data-[state=open]:shadow-sm"
                  data-testid={`faq-item-${i}`}
                >
                  <AccordionTrigger className="text-left font-semibold hover:no-underline py-4">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed pb-4">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </div>
      </section>

      {/* Still have questions CTA */}
      <section className="py-16 bg-muted/30 border-t">
        <div className="container mx-auto px-4 max-w-2xl text-center">
          <MessageCircle className="h-10 w-10 text-primary mx-auto mb-3" />
          <h2 className="text-2xl sm:text-3xl font-bold mb-3">
            Still have questions?
          </h2>
          <p className="text-muted-foreground mb-6">
            Can't find the answer you're looking for? We usually reply within a few hours.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              onClick={() => (window.location.href = 'mailto:support@vibesprint.ai')}
              className="btn-cta"
              data-testid="faq-contact-button"
            >
              <Mail className="h-4 w-4 mr-2" />
              Email support
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/pricing')}
              data-testid="faq-pricing-button"
            >
              View pricing
            </Button>
          </div>
        </div>
      </section>

      <Footer variant="minimal" />
    </div>
  );
};

export default FAQPage;
